import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '';

// Global, device-level theme preference. Persisted in localStorage so it
// follows the user across EVERY role (guest / host / venue / staff / admin)
// and across reloads. Three modes:
//   'system' (default) → follow the phone/OS preference
//   'light'            → always light
//   'dark'             → always dark
// "system" is what protects Samsung force-dark users: when their phone is
// dark we serve our own designed dark theme instead of an ugly inversion.
const STORAGE_KEY = 'allure_theme';
const PALETTE_STORAGE_KEY = 'allure_palette';

// Curated palettes — each swaps the accent family + premium-shadow tint.
// 'default' = Allure signature red identity.
// Every palette is designed to work in BOTH light and dark mode.
export const PALETTES = [
  {
    id: 'default',
    name: 'Allure Red',
    tagline: 'The Allure signature',
    swatches: ['#C41230', '#E8192C', '#FFF0F1'],
  },
  {
    id: 'aurora',
    name: 'Aurora Pink',
    tagline: 'Romantic & vivid',
    swatches: ['#E91E63', '#FF4D8F', '#FFE4EE'],
  },
  {
    id: 'sunset',
    name: 'Sunset Glow',
    tagline: 'Warm & energetic',
    swatches: ['#F97316', '#FB923C', '#FFEDD5'],
  },
  {
    id: 'purple',
    name: 'Midnight Purple',
    tagline: 'Deep & electric',
    swatches: ['#703FF6', '#7341FE', '#F4F0FF'],
  },
  {
    id: 'ocean',
    name: 'Ocean Drift',
    tagline: 'Cool & focused',
    swatches: ['#0EA5E9', '#22D3EE', '#E0F4FE'],
  },
  {
    id: 'forest',
    name: 'Forest Calm',
    tagline: 'Grounded & natural',
    swatches: ['#10B981', '#34D399', '#DCFCE7'],
  },
];

const PALETTE_IDS = new Set(PALETTES.map((p) => p.id));

const ThemeContext = createContext(null);

const getSystemDark = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const readStoredPref = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch { /* noop */ }
  return 'system';
};

const readStoredPalette = () => {
  try {
    const v = localStorage.getItem(PALETTE_STORAGE_KEY);
    if (v && PALETTE_IDS.has(v)) return v;
  } catch { /* noop */ }
  return 'default';
};

const applyResolved = (resolved) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (resolved === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  root.style.colorScheme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolved === 'dark' ? '#121217' : '#F5F5F5');
};

const applyPalette = (palette) => {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.palette = palette;
};

export const ThemeProvider = ({ children }) => {
  const [pref, setPrefState] = useState(readStoredPref);
  const [palette, setPaletteState] = useState(readStoredPalette);
  const [systemDark, setSystemDark] = useState(getSystemDark);
  const [isToggling, setIsToggling] = useState(false);
  const [togglingToDark, setTogglingToDark] = useState(false);
  const toggleTimerRef = useRef(null);
  const swapTimerRef = useRef(null);
  // Used to suppress the initial sync-back to the server when the server's
  // own value is what we just applied (avoids a redundant PUT loop).
  const skipNextServerPushRef = useRef(false);
  const hasHydratedFromServerRef = useRef(false);

  const resolved = pref === 'system' ? (systemDark ? 'dark' : 'light') : pref;

  // ------------------------------------------------------------------
  // TEMPORARY LIGHT-MODE LOCK (2026-02)
  // The whole app is force-served in LIGHT mode for A/B testing. The
  // dark-mode CSS stays intact — we simply pin the RESOLVED value to
  // 'light' so nothing on the page ever gets the `.dark` class. All
  // stored user preferences (localStorage + server) are preserved so
  // they resume automatically the moment this override is removed.
  // ------------------------------------------------------------------
  const resolvedForRender = 'light';
  void resolved; // keep the memoized value alive for future re-enabling

  useEffect(() => {
    applyResolved(resolvedForRender);
  }, [resolvedForRender]);

  useEffect(() => {
    applyPalette(palette);
  }, [palette]);

  // Hydrate from the server once a token is available. If the server has a
  // different choice than localStorage (because the user picked it on
  // another device), we adopt the server's value. Otherwise we keep local.
  useEffect(() => {
    if (!API) return undefined;
    let cancelled = false;
    const hydrate = async () => {
      const token = (() => {
        try { return localStorage.getItem('token'); } catch { return null; }
      })();
      if (!token) return;
      try {
        const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        const serverTheme = res.data?.appearance_theme;
        const serverPalette = res.data?.appearance_palette;
        if (serverTheme && (serverTheme === 'light' || serverTheme === 'dark' || serverTheme === 'system')) {
          if (serverTheme !== pref) {
            skipNextServerPushRef.current = true;
            setPrefState(serverTheme);
            try { localStorage.setItem(STORAGE_KEY, serverTheme); } catch { /* noop */ }
          }
        }
        if (serverPalette && PALETTE_IDS.has(serverPalette)) {
          if (serverPalette !== palette) {
            skipNextServerPushRef.current = true;
            setPaletteState(serverPalette);
            try { localStorage.setItem(PALETTE_STORAGE_KEY, serverPalette); } catch { /* noop */ }
          }
        }
      } catch {
        /* offline / expired token / 401 — local choice stays in effect */
      } finally {
        hasHydratedFromServerRef.current = true;
      }
    };
    hydrate();
    // Re-hydrate when the user logs in (token appears after sign-in).
    // `storage` only fires for OTHER tabs; the in-tab signal comes from
    // App.handleLogin dispatching `allure:auth-changed`.
    const onStorage = (e) => {
      if (e.key === 'token' && e.newValue) hydrate();
    };
    const onAuthChanged = () => hydrate();
    window.addEventListener('storage', onStorage);
    window.addEventListener('allure:auth-changed', onAuthChanged);
    return () => {
      cancelled = true;
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('allure:auth-changed', onAuthChanged);
    };
  }, []);

  // Push changes to the server (fire-and-forget). Only runs AFTER hydration
  // completes so we don't overwrite the server's value on first paint.
  const pushToServer = useCallback((payload) => {
    if (!API) return;
    if (skipNextServerPushRef.current) {
      skipNextServerPushRef.current = false;
      return;
    }
    if (!hasHydratedFromServerRef.current) return;
    const token = (() => {
      try { return localStorage.getItem('token'); } catch { return null; }
    })();
    if (!token) return;
    axios
      .put(`${API}/account/settings/appearance`, payload, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => { /* fire-and-forget; local copy is the source of truth */ });
  }, []);

  // Track OS preference changes so 'system' mode stays in sync live.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemDark(e.matches);
    try { mq.addEventListener('change', handler); } catch { mq.addListener(handler); }
    return () => {
      try { mq.removeEventListener('change', handler); } catch { mq.removeListener(handler); }
    };
  }, []);

  const setPref = useCallback((next) => {
    // Determine which direction we're toggling
    const nextDark = next === 'dark' || (next === 'system' && getSystemDark());
    setTogglingToDark(nextDark);
    setIsToggling(true);
    document.documentElement.classList.add('theme-is-toggling');
    document.documentElement.classList.add(nextDark ? 'toggling-to-dark' : 'toggling-to-light');
    if (toggleTimerRef.current) clearTimeout(toggleTimerRef.current);
    if (swapTimerRef.current) clearTimeout(swapTimerRef.current);

    // Delay flipping the actual `dark` class (and committing the
    // preference) until the metallic sweep is at its brightest specular
    // peak (~600ms into the 1.4s wipe). Swapping the theme exactly when
    // the overlay is at peak whiteness masks the underlying card / page
    // colour change so the user only sees the polished sweep — never a
    // blink between old and new theme.
    const SWAP_AT_MS = 600;
    const END_AT_MS = 1400;

    swapTimerRef.current = setTimeout(() => {
      setPrefState(next);
      try { localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
      pushToServer({ theme: next });
    }, SWAP_AT_MS);

    toggleTimerRef.current = setTimeout(() => {
      setIsToggling(false);
      document.documentElement.classList.remove('theme-is-toggling');
      document.documentElement.classList.remove('toggling-to-dark');
      document.documentElement.classList.remove('toggling-to-light');
    }, END_AT_MS);
  }, [pushToServer]);

  const setPalette = useCallback((next) => {
    if (!PALETTE_IDS.has(next)) return;
    setPaletteState(next);
    try { localStorage.setItem(PALETTE_STORAGE_KEY, next); } catch { /* noop */ }
    pushToServer({ palette: next });
  }, [pushToServer]);

  const value = useMemo(
    () => ({ pref, setPref, resolved: resolvedForRender, isDark: false, palette, setPalette, palettes: PALETTES, isToggling, togglingToDark }),
    [pref, setPref, resolvedForRender, palette, setPalette, isToggling, togglingToDark],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      pref: 'system', setPref: () => {}, resolved: 'light', isDark: false,
      palette: 'default', setPalette: () => {}, palettes: PALETTES,
      isToggling: false, togglingToDark: false,
    };
  }
  return ctx;
};
